import moment from 'moment';
import { ERRange } from './type';
import Highcharts from 'highcharts';

export const getColors = (index: number): string => ['#00324a', '#2c9ab7'][index] || 'lightgrey';

export const getDisplayRange = (
  range: ERRange,
  dataStartDate: number,
  dataEndDate: number,
): [ERRange, boolean] => {
  let isDifferentFromRange = false;
  let minX: number;
  let maxX: number;

  if (!(range && range.start && range.end)) {
    return [{ start: null, end: null }, false];
  }

  // Selected graph range
  // transforming range values to catch all of the start day and all of the end day
  const rangeStart = moment.utc(range.start).startOf('day').valueOf();
  const rangeEnd = moment.utc(range.end).endOf('day').valueOf();

  // Saved graph range
  const initialRangeStart = moment.utc(unixToJSDate(dataStartDate)).startOf('day').valueOf();
  const initialRangeEnd = moment.utc(unixToJSDate(dataEndDate)).endOf('day').valueOf();

  // we need to examine whether range.start and range.end are equivalent dates
  // to chartOptions.start_date and chartOptions.end_date
  // if they aren't equal, user has made a datepicker change and we shouldn't enforce series limit
  // if they are equal, enforce initial series limit

  // check if selected range differs from saved graph range.
  if (range.start) {
    if (rangeStart === initialRangeStart && rangeEnd === initialRangeEnd) {
      isDifferentFromRange = false;
    } else {
      isDifferentFromRange = true;
    }
    minX = rangeStart;
    maxX = rangeEnd;
  } else {
    minX = initialRangeStart;
    maxX = initialRangeEnd;
  }

  return [{ start: minX, end: maxX }, isDifferentFromRange];
};

export const getSeriesName = (index: number, numTeams: number, translation: Function): string => {
  const t = translation;
  return [`All ${numTeams ? numTeams : ''} teams with current ${t('OKRs')}`, 'Teams with engagement & transparency'][index] || `Series ${index + 1}`;
};

export const getTooltipText = (point: any, week: number, translation: Function, debug: boolean): string => {
  const t = translation,
    y1 = point.series.chart.series[0].processedYData[point.point.index],
    y2 = point.series.chart.series[1].processedYData[point.point.index];
  let dbgText = '';

  const getTooltipPostText = (idx) => [`current ${t('OKRs')}`, 'engagement & transparency'][idx] || 'unknown';

  if (debug) {
    const labelTime = moment(point.x).utc().valueOf();
    const date = Highcharts.dateFormat('%e %b %Y', labelTime).trim();
    dbgText = ` (${date})`;
  }

  if (y1 === y2) {
    // Combo tooltip for overlapping data points
    return `Week ${week + 1}${dbgText}:&nbsp;&nbsp;`
      + ` ${y1} teams with ${getTooltipPostText(0)} and`
      + ` ${y2} teams with ${getTooltipPostText(1)}`;
  } else {
    const y = point.y.toLocaleString(), series = point.series.index;
    return `Week ${week + 1}${dbgText}:&nbsp;&nbsp;${y} teams with ${getTooltipPostText(series)}`;
  }
};

export const jsToUnixDate = (date: number): number => {
  return Math.floor(date) / 1000;
};

export const outOfRange = (val: number, range: ERRange): boolean => {
  return (val >= range.start && val <= range.end) ? true : false;
};

export const rangeToDateRange = (filter: ERRange): ERRange | null => {
  if (!(filter && filter.start && filter.end)) {
    return null;
  }
  const start = moment(filter.start, 'MM/DD/YYYY').valueOf(),
    end = moment(filter.end, 'MM/DD/YYYY').valueOf();
  return { start: start, end: end };
};

export const rangeToString = (range: ERRange, unix: boolean): string => {
  if (!range.start || !range.end) {
    return 'Invalid range';
  }

  const start = unix ? unixToJSDate(range.start) : range.start;
  const end = unix ? unixToJSDate(range.end) : range.end;
  return `start: ${moment(start).toLocaleString()}, end: ${moment(end).toLocaleString()}`;
};

export const unixToJSDate = (date: number): number => {
  return date * 1000;
};
